import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
  createSensor,
  deleteSensor,
  fetchDetailSensor,
  fetchSensorList,
  updateSensor,
} from '@/domains/asset-management/apis/sensor-management';
import type {
  AIRSENSORID,
  CreateSensorPayload,
  CreateSensorResponse,
  DeleteSensorResponse,
  SensorDetailResponse,
  SensorListResponse,
  UpdateSensorParams,
  UpdateSensorPayload,
  UpdateSensorResponse,
  UseSensorManagementProps,
} from '@/domains/asset-management/types/sensor';
import NoticeMessage from '@/plugin/noticemessage/noticemessage.js';

const useSensorManagement = ({
  airSensorID = null,
  queryParams = '',
  onDeleteSuccess = () => {},
  onCreateSuccess = () => {},
  onUpdateSuccess = () => {},
}: UseSensorManagementProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: sensorListData } = useQuery<SensorListResponse>({
    queryKey: ['sensorListData', queryParams],
    queryFn: () => fetchSensorList(queryParams),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const { data: detailSensorData, refetch: refetchDetailSensor } = useQuery<SensorDetailResponse>({
    queryKey: ['detailSensorData', airSensorID],
    queryFn: () => fetchDetailSensor(airSensorID as AIRSENSORID),
    retry: false,
    enabled: !!airSensorID,
  });

  const createSensorMutation = useMutation<CreateSensorResponse, Error, CreateSensorPayload>({
    mutationFn: (sensorData) => createSensor(sensorData),
    onSuccess: (responseData) => {
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['sensorListData', queryParams],
          });
          onCreateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      new NoticeMessage(t(message));
    },
  });

  const deleteSensorMutation = useMutation<DeleteSensorResponse, Error, AIRSENSORID>({
    mutationFn: (airSensorID) => deleteSensor(airSensorID),
    onSuccess: () => {
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
          queryClient.invalidateQueries({
            queryKey: ['sensorListData', queryParams],
          });
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      new NoticeMessage(t(message));
    },
  });

  const updateSensorMutation = useMutation<
    UpdateSensorResponse,
    Error,
    {
      sensorData: UpdateSensorPayload;
      params: UpdateSensorParams;
    }
  >({
    mutationFn: ({ sensorData, params }) => updateSensor(sensorData, params),
    onSuccess: (responseData) => {
      new NoticeMessage(t('msg > update success'), {
        callback() {
          queryClient.invalidateQueries({
            queryKey: ['sensorListData', queryParams],
          });
          queryClient.invalidateQueries({
            queryKey: ['detailSensorData', responseData.data],
          });
          onUpdateSuccess(responseData.data);
        },
      });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ message: string }>;
      const message = axiosError.response?.data?.message || axiosError.message;
      new NoticeMessage(t(message));
    },
  });

  return {
    sensorListData,
    detailSensorData,
    refetchDetailSensor,
    createSensor: createSensorMutation.mutate,
    updateSensor: updateSensorMutation.mutate,
    deleteSensor: deleteSensorMutation.mutate,
  };
};

export default useSensorManagement;
